import React, { useContext, useMemo, useState } from "react";
import CoinLogo from "./CoinLogo";
import { ThemeContext } from "../index";

export default function NewsFlash({ news, title = "CMD News Flash" }) {
  const [theme] = useContext(ThemeContext);
  const [sortMode, setSortMode] = useState("alpha"); // "alpha" | "latest" | "sentiment"

  const sentimentClass = (s) => {
    if (!s) return "neutral";
    const v = String(s).toLowerCase();
    if (v === "bullish") return "bullish";
    if (v === "bearish") return "bearish";
    return "neutral";
  };

  const sortedNews = useMemo(() => {
    const copy = [...(news || [])];
    if (sortMode === "alpha") {
      copy.sort((a, b) => {
        const A = (a.coin_pair_name || "").localeCompare(b.coin_pair_name || "");
        if (A !== 0) return A;
        // tie-breaker: newest first
        return new Date(b.date) - new Date(a.date);
      });
    } else if (sortMode === "latest") {
      copy.sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (sortMode === "sentiment") {
      const rank = { bullish: 0, neutral: 1, bearish: 2 };
      copy.sort((a, b) => {
        const ra = rank[(a.headline_sentiment || "neutral").toLowerCase()] ?? 1;
        const rb = rank[(b.headline_sentiment || "neutral").toLowerCase()] ?? 1;
        if (ra !== rb) return ra - rb;
        return new Date(b.date) - new Date(a.date);
      });
    }
    return copy;
  }, [news, sortMode]);

  return (
    <div className="newsflash-container">
      {/* Header + sort controls */}
      <div
  className="newsflash-header"
  style={{ color: theme?.foreground || "black" }}
>
  {/* Title on its own line */}
  <span className="newsflash-title">{title}</span>

  {/* Filters below the title */}
  <div className="newsflash-sort">
    <span className="sort-label">Sort by:</span>
    <label className="radio">
      <input
        type="radio"
        name="news-sort"
        value="alpha"
        checked={sortMode === "alpha"}
        onChange={() => setSortMode("alpha")}
      />
      <span>Alphabetical</span>
    </label>
    <label className="radio">
      <input
        type="radio"
        name="news-sort"
        value="latest"
        checked={sortMode === "latest"}
        onChange={() => setSortMode("latest")}
      />
      <span>Latest</span>
    </label>
    <label className="radio">
      <input
        type="radio"
        name="news-sort"
        value="sentiment"
        checked={sortMode === "sentiment"}
        onChange={() => setSortMode("sentiment")}
      />
      <span>Sentiment</span>
    </label>
  </div>

  <style jsx>{`
    .newsflash-header {
      padding: 8px 12px;
      font-weight: 700;
      font-size: 16px;
      border-bottom: 1px solid rgba(128, 128, 128, 0.5);
      text-align: left;
    }

    .newsflash-title {
      display: block;
      margin-bottom: 6px;
    }

    .newsflash-sort input[type="radio"]:checked {
      accent-color: #007bff;
    }

    .sort-label {
      font-size: 13px;
      font-weight: 500;
      color: gray;
    }

    .radio {
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
    }

    .radio input {
      accent-color: blue; /* native support for modern browsers */
    }

    .radio span {
      font-size: 13px;
    }
  `}</style>
</div>



      {/* Scrollable list */}
      <div className="newsflash-list">
        {sortedNews.map((n) => (
          <div key={n.id} className="newsflash-item">
            {/* Time + logo + coin */}
            <div className="newsflash-row">
              <div className="newsflash-logo">
                <CoinLogo coinPair={n.coin_pair_name} />
              </div>
              <span
                className="newsflash-coin"
                style={{ color: theme?.foreground || "black" }}
              >
                {n.coin_pair_name}
              </span>
              <span className="newsflash-time" style={{ color: theme?.foreground || "black" }}>
                {n.date_ago}
              </span>
            </div>

            {/* Headline with sentiment dot */}
            <div className="headline-row">
              <span
                className={`sentiment-dot ${sentimentClass(n.headline_sentiment)}`}
                aria-label={`sentiment ${n.headline_sentiment || "neutral"}`}
              />
              <a
                href={`https://cryptomarketdarts.com/public_prediction.php?id=${n.id}`}
                target="_blank"
                rel="noreferrer"
                className="newsflash-headline"
                style={{ color: theme?.foreground || "black" }}
              >
                {n.headline}
              </a>
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        .newsflash-container {
          width: 100%;
          max-width: 560px;
        }

        .newsflash-header {
          display: flex;
          flex-direction: column; /* stack title and filters */
          align-items: flex-start; /* left align */
          gap: 6px;
          padding: 8px 12px;
          font-weight: 700;
          font-size: 16px;
          border-bottom: 1px solid rgba(128, 128, 128, 0.5);
        }

        .newsflash-title {
          color: inherit;
        }

        .newsflash-sort {
          display: flex;
          gap: 12px;
          font-weight: 500;
          font-size: 13px;
        }

        .radio {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          user-select: none;
        }
        .radio input {
          accent-color: #ff3b3b; /* red accent for radios */
          cursor: pointer;
        }

        .newsflash-list {
          height: 300px;
          overflow-y: auto;
          padding: 0;
          border: 1px solid rgba(128, 128, 128, 0.5);
          border-radius: 8px;
          background: transparent;
        }

        .newsflash-item {
          padding: 10px 12px;
          border-bottom: 1px solid rgba(128, 128, 128, 0.5);
          text-align: left;
        }

        .newsflash-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 6px;
        }

        .newsflash-time {
          font-size: 12px;
          line-height: 18px;
          margin-left: auto;
        }

        .newsflash-coin {
          font-size: 12px;
          font-weight: 600;
          line-height: 18px;
        }

        .headline-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sentiment-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          display: inline-block;
          flex: 0 0 auto;
          border: 1px solid rgba(0, 0, 0, 0.25);
        }
        .sentiment-dot.bullish {
          background: #00b050;
          border-color: #00b050;
        }
        .sentiment-dot.bearish {
          background: #d9534f;
          border-color: #d9534f;
        }
        .sentiment-dot.neutral {
          background: #808080;
          border-color: #808080;
        }

        .newsflash-headline {
          text-decoration: none;
          font-size: 15px;
          line-height: 1.25;
          display: inline-block;
        }

        .newsflash-list::-webkit-scrollbar {
          width: 6px;
        }
        .newsflash-list::-webkit-scrollbar-thumb {
          background: rgba(128, 128, 128, 0.5);
          border-radius: 3px;
        }
        .newsflash-list::-webkit-scrollbar-track {
          background: transparent;
        }
      `}</style>
    </div>
  );
}
